<template>
    <div>
        <h6 class="mb-3">{{ title }}</h6>
        <div class="card shadow border-0">
            <ul class="list-group list-group-flush">
                <li v-if="templates.length === 0 && editingTemplateId !== 'new'" class="list-group-item">
                    <div class="text-muted small fst-italic">{{ emptyText }}</div>
                </li>

                <li v-for="template in templates" :key="template.id" class="list-group-item">
                    <template v-if="editingTemplateId === template.id">
                        <div class="mb-2">
                            <label class="form-label text-muted extra-small text-uppercase mb-1">{{ $t('profile.templateName') }}</label>
                            <input v-model="templateForm.name" type="text" class="form-control form-control-sm"
                                :placeholder="$t('profile.templateName')">
                        </div>
                        <label class="form-label text-muted extra-small text-uppercase mb-1">{{ $t('profile.templateDescription') }}</label>
                        <textarea v-model="templateForm.description" rows="3" class="form-control form-control-sm"
                            :placeholder="$t('profile.templateDescription')"></textarea>
                        <div class="d-flex justify-content-end mt-2">
                            <button class="btn btn-sm btn-light me-2" :disabled="savingTemplate"
                                @click="cancelEditTemplate">{{ $t('common.cancel') }}</button>
                            <button class="btn btn-sm btn-primary" :disabled="savingTemplate"
                                @click="saveTemplate">{{ $t('common.save') }}</button>
                        </div>
                    </template>
                    <template v-else>
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="fw-medium me-2 text-break">{{ template.name }}</div>
                            <div v-if="confirmingDeleteId === template.id"
                                class="d-flex align-items-center flex-shrink-0">
                                <span class="text-danger small me-2">{{ $t('profile.deleteTemplateConfirm') }}</span>
                                <button class="btn btn-sm btn-light me-2" :disabled="savingTemplate"
                                    @click="confirmingDeleteId = null">{{ $t('common.cancel') }}</button>
                                <button class="btn btn-sm btn-danger" :disabled="savingTemplate"
                                    @click="deleteTemplate(template)">{{ $t('common.yes') }}</button>
                            </div>
                            <div v-else class="d-flex flex-shrink-0">
                                <button class="btn-icon bg-primary-subtle text-primary me-2" :title="$t('common.edit')"
                                    @click="startEditTemplate(template)"><i class="fa-regular fa-pen-to-square"></i></button>
                                <button class="btn-icon bg-danger-subtle text-danger" :title="$t('common.delete')"
                                    @click="confirmingDeleteId = template.id"><i class="fa-regular fa-trash-can"></i></button>
                            </div>
                        </div>
                        <div class="text-muted small mt-1 text-break template-description">{{ template.description }}</div>
                    </template>
                </li>

                <li v-if="editingTemplateId === 'new'" class="list-group-item">
                    <div class="mb-2">
                        <label class="form-label text-muted extra-small text-uppercase mb-1">{{ $t('profile.templateName') }}</label>
                        <input v-model="templateForm.name" type="text" class="form-control form-control-sm"
                            :placeholder="$t('profile.templateName')">
                    </div>
                    <label class="form-label text-muted extra-small text-uppercase mb-1">{{ $t('profile.templateDescription') }}</label>
                    <textarea v-model="templateForm.description" rows="3" class="form-control form-control-sm"
                        :placeholder="$t('profile.templateDescription')"></textarea>
                    <div class="d-flex justify-content-end mt-2">
                        <button class="btn btn-sm btn-light me-2" :disabled="savingTemplate"
                            @click="cancelEditTemplate">{{ $t('common.cancel') }}</button>
                        <button class="btn btn-sm btn-primary" :disabled="savingTemplate"
                            @click="saveTemplate">{{ $t('common.save') }}</button>
                    </div>
                </li>
            </ul>
        </div>
        <div class="d-flex justify-content-end mt-2">
            <div v-if="templates.length >= max" class="text-muted small fst-italic">
                {{ limitText }}
            </div>
            <button v-else-if="editingTemplateId === null" class="btn btn-sm btn-primary"
                @click="startAddTemplate">{{ addLabel }}</button>
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';

export default {
    name: 'ProfileTemplateList',
    props: {
        templates: {
            type: Array,
            required: true
        },
        // USER_TEXT_TEMPLATE_TYPE in php/constants.php: 1 = description quick-picks,
        // 2 = email bodies. Sent on create only -- see saveTemplate().
        typeId: {
            type: Number,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        emptyText: {
            type: String,
            required: true
        },
        addLabel: {
            type: String,
            required: true
        },
        // Already interpolated with the count by the parent, so each list can name
        // itself in the message.
        limitText: {
            type: String,
            required: true
        },
        max: {
            type: Number,
            required: true
        }
    },
    emits: ['update:templates'],
    data() {
        return {
            // Per instance, so opening a form in one list leaves the other alone.
            editingTemplateId: null,
            templateForm: { name: '', description: '' },
            savingTemplate: false,
            confirmingDeleteId: null,
        };
    },
    methods: {
        startEditTemplate(template) {
            this.confirmingDeleteId = null;
            this.editingTemplateId = template.id;
            this.templateForm = {
                name: template.name || '',
                description: template.description || ''
            };
        },
        startAddTemplate() {
            this.confirmingDeleteId = null;
            this.editingTemplateId = 'new';
            this.templateForm = { name: '', description: '' };
        },
        cancelEditTemplate() {
            this.editingTemplateId = null;
            this.templateForm = { name: '', description: '' };
        },
        async saveTemplate() {
            const name = this.templateForm.name.trim();
            const description = this.templateForm.description.trim();
            if (!name || !description) {
                this.$root.$refs.globalAlert?.showAlert(this.$t('profile.templateFieldsRequired'), 'danger');
                return;
            }
            const isNew = this.editingTemplateId === 'new';
            this.savingTemplate = true;
            try {
                // type_id on create only. Omitting it on PUT makes the server keep the
                // row's current type, so a list can never convert a row it does not own.
                const response = isNew
                    ? await axios.post('/users/profile/text-templates', { name, description, type_id: this.typeId })
                    : await axios.put(`/users/profile/text-templates/${this.editingTemplateId}`, { name, description });
                const saved = response.data.data;
                const list = [...this.templates];
                const index = list.findIndex(t => t.id === saved.id);
                if (index === -1) {
                    list.push(saved);
                } else {
                    list.splice(index, 1, saved);
                }
                // Keep the local order matching the API's ORDER BY name ASC so a
                // reload does not reshuffle the list the user just edited.
                list.sort((a, b) => a.name.localeCompare(b.name));
                this.$emit('update:templates', list);
                this.cancelEditTemplate();
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.saved'), 'success');
            } catch (error) {
                console.error('Error saving text template:', error);
                // Server message first: this is how the 409 past the per-type cap
                // reaches the user in its own words.
                const message = error?.response?.data?.error?.message || this.$t('common.saveFailed');
                this.$root.$refs.globalAlert?.showAlert(message, 'danger');
            } finally {
                this.savingTemplate = false;
            }
        },
        async deleteTemplate(template) {
            this.savingTemplate = true;
            try {
                await axios.delete(`/users/profile/text-templates/${template.id}`);
                this.$emit('update:templates', this.templates.filter(t => t.id !== template.id));
                this.confirmingDeleteId = null;
                this.$root.$refs.globalAlert?.showAlert(this.$t('profile.templateDeleted'), 'success');
            } catch (error) {
                console.error('Error deleting text template:', error);
                const message = error?.response?.data?.error?.message || this.$t('common.failed');
                this.$root.$refs.globalAlert?.showAlert(message, 'danger');
            } finally {
                this.savingTemplate = false;
            }
        },
    }
};
</script>

<style scoped>
.template-description {
    white-space: pre-wrap;
}
</style>
